import { create } from 'zustand'
import { campaignsApi, type PendingCampaignApproval } from '@/lib/api'
import { toast } from 'sonner'

interface CampaignApprovalsStore {
  pending: PendingCampaignApproval[]
  loading: boolean

  fetchPending: () => Promise<void>
  approve: (campaignId: number) => Promise<void>
  // The reason is required: the Rep sees it on the rejected campaign.
  reject: (campaignId: number, reason: string) => Promise<void>
  reset: () => void
}

const initialState = {
  pending: [],
  loading: false,
}

export const useCampaignApprovalsStore = create<CampaignApprovalsStore>((set, get) => ({
  ...initialState,

  fetchPending: async () => {
    set({ loading: true })
    try {
      const response = await campaignsApi.pendingApprovals()
      set({ pending: response.data, loading: false })
    } catch {
      // Expected for any role other than Owner/Admin/Manager (403) -- see
      // CampaignApprovalsSection, which never calls this without checking
      // usePermission first, so a failure here is unusual, not routine.
      set({ loading: false })
    }
  },

  approve: async (campaignId: number) => {
    try {
      const response = await campaignsApi.approveLaunch(campaignId)
      set({ pending: get().pending.filter((p) => p.campaign_id !== campaignId) })
      // Approving a campaign scheduled for later hands it back to the
      // scheduler rather than sending now — say which happened, since
      // "Campaign launched" would be wrong (and alarming) for a future send.
      toast.success(
        response.data?.status === 'running'
          ? 'Campaign launched'
          : 'Launch approved. The campaign sends at its scheduled time.',
      )
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve launch')
      throw error
    }
  },

  reject: async (campaignId: number, reason: string) => {
    try {
      await campaignsApi.rejectLaunch(campaignId, reason)
      set({ pending: get().pending.filter((p) => p.campaign_id !== campaignId) })
      toast.success('Launch request rejected')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject launch')
      throw error
    }
  },

  reset: () => set(initialState),
}))
